import { defineStore } from 'pinia';
import { ref, watch, computed, Ref } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { FormTemplateDTO, IFormTemplateDTO, IPaginationDTO, PaginationDTO } from '@/dtos';
import {
  getAllFormTemplatesAPI,
  getFormTemplateByIdAPI,
  updateFormTemplateAPI,
} from '@/services/admin';
import { useUpdateStatusStore } from './updateStatus';
import { getDefaultTermsFormTemplatedAPI, getFormTemplateAPI } from '@/services';
import { useAuthStore } from './auth';
export const DEFAULT_TERMS_AND_CONDITIONS_FORM = computed(
  () => 'aef0a6aa-f6bc-4f1e-b0cd-02996a72fc57',
);
export const DEFAULT_TALENT_FORM = computed(() => '144f777d-c047-43ca-9383-f885bfda0912');
export const DEFAULT_MUSIC_FORM = computed(() => 'a05c4fe5-23bb-4648-bc58-8be397f48817');
export const DEFAULT_LOCATION_FORM = computed(() => '1bdd7764-037b-4db6-89e3-7b7fd2bdd9e5');
export const useFormTemplateStore = defineStore('formTemplate', () => {
  const queryClient = useQueryClient();
  const statusStore = useUpdateStatusStore();
  const authStore = useAuthStore();
  const formTemplateId = ref<string | undefined>(undefined);
  const pagination = ref<IPaginationDTO>(new PaginationDTO());
  const paginationQueryKey = computed(() => ['form-templates', pagination.value]);
  const fetchFormTemplates = async (): Promise<IFormTemplateDTO[]> => {
    const ret = await getAllFormTemplatesAPI(pagination.value);
    const forms = await Promise.all(ret.data.map((form) => new FormTemplateDTO(form)));
    return forms;
  };

  const getFormTemplateById = (formTemplateId: Ref<string>) =>
    useQuery({
      queryKey: computed(() => ['form-template', formTemplateId]),
      queryFn: async (): Promise<IFormTemplateDTO> => {
        if (!formTemplateId.value) {
          return new FormTemplateDTO();
        }

        let formTemplate;
        if (authStore.isAdmin) {
          formTemplate = await getFormTemplateByIdAPI(formTemplateId.value);
        } else {
          formTemplate = await getFormTemplateAPI(formTemplateId.value);
        }
        return new FormTemplateDTO(formTemplate);
      },
      enabled: computed(() => !!formTemplateId.value),
    });

  const formTemplatePaginationQuery = useQuery({
    queryKey: paginationQueryKey,
    queryFn: fetchFormTemplates,
    enabled: computed(() => !!pagination.value && authStore.isAdmin),
  });

  const getDefaultTermsFormTemplate = useQuery({
    queryKey: ['form-template-default-terms'],
    queryFn: async (): Promise<IFormTemplateDTO> => {
      return await getDefaultTermsFormTemplatedAPI();
    },
  });

  const update = useMutation({
    mutationFn: async (template: IFormTemplateDTO) => {
      return await updateFormTemplateAPI(template.id, template);
    },
    onSuccess: (data, variables) => {
      statusStore.setSuccessStatus(`form-template-${variables.id}`, 'success:update');
      if (data && data.id != '') {
        queryClient.setQueryData(['form-template', data.id], data);
      }
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`form-template-${variables.id}`, err);
    },
  });

  watch(pagination, () => {
    formTemplatePaginationQuery.refetch();
  });

  const refetchTemplates = () => {
    formTemplatePaginationQuery.refetch();
  };

  const setPagination = (newPagination: IPaginationDTO) => {
    pagination.value = newPagination;
  };

  return {
    pagination,
    formTemplatePaginationQuery,
    update,
    getFormTemplateById,
    refetchTemplates,
    setPagination,
    getDefaultTermsFormTemplate,
  };
});
